import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Users, 
  ThumbsUp,
  Clock,
  CheckCircle,
  Loader2,
  Plus,
  ArrowRight,
  Code
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CommunityPage = () => {
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [votingId, setVotingId] = useState(null);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/submissions`);
      setSubmissions(response.data);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (submissionId) => {
    if (!user) {
      toast.error('Please sign in to vote');
      return;
    }
    
    setVotingId(submissionId);
    try {
      await axios.post(`${API}/submissions/${submissionId}/vote`);
      toast.success('Vote recorded!');
      fetchSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to vote');
    } finally {
      setVotingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-950/30 text-amber-400 border-amber-900/50',
      approved: 'bg-emerald-950/30 text-emerald-400 border-emerald-900/50',
      rejected: 'bg-red-950/30 text-red-400 border-red-900/50',
    };
    return styles[status] || 'bg-slate-800 text-slate-300 border-slate-700';
  };

  const pendingSubmissions = submissions.filter(s => s.status === 'pending');
  const approvedSubmissions = submissions.filter(s => s.status === 'approved');

  return (
    <div className="pt-20 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-12">
          <div>
            <Badge className="mb-4 bg-purple-950/30 text-purple-400 border-purple-900/50">
              <Users className="w-3.5 h-3.5 mr-1.5" />
              Community
            </Badge>
            <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-white mb-4">
              Community Connectors
            </h1>
            <p className="text-lg text-slate-400">
              Connectors built and submitted by the community. Vote for your favorites!
            </p>
          </div>
          <Link to="/submit">
            <Button className="glow-primary bg-primary hover:bg-primary/90" data-testid="submit-connector-btn">
              <Plus className="w-5 h-5 mr-2" />
              Submit Connector
            </Button>
          </Link>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="pending" className="space-y-8">
          <TabsList className="bg-secondary">
            <TabsTrigger value="pending" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Pending Review ({pendingSubmissions.length})
            </TabsTrigger>
            <TabsTrigger value="approved" className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Approved ({approvedSubmissions.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            {loading ? (
              <div className="flex items-center justify-center py-24">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
            ) : pendingSubmissions.length === 0 ? (
              <div className="text-center py-24">
                <div className="w-16 h-16 rounded-full bg-secondary mx-auto mb-6 flex items-center justify-center">
                  <Code className="w-8 h-8 text-slate-500" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-white mb-2">No pending submissions</h3>
                <p className="text-slate-400 mb-6">Be the first to submit a connector!</p>
                <Link to="/submit">
                  <Button className="bg-primary hover:bg-primary/90">
                    Submit Connector
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingSubmissions.map((submission) => (
                  <SubmissionCard 
                    key={submission.id} 
                    submission={submission} 
                    onVote={handleVote}
                    votingId={votingId}
                    user={user}
                    getStatusBadge={getStatusBadge}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="approved">
            {approvedSubmissions.length === 0 ? (
              <div className="text-center py-24">
                <p className="text-slate-400">No approved community connectors yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {approvedSubmissions.map((submission) => (
                  <SubmissionCard 
                    key={submission.id} 
                    submission={submission} 
                    onVote={handleVote}
                    votingId={votingId}
                    user={user}
                    getStatusBadge={getStatusBadge}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const SubmissionCard = ({ submission, onVote, votingId, user, getStatusBadge }) => {
  return (
    <div 
      className="bg-card border border-white/5 rounded-xl p-6 hover:border-white/10 transition-colors"
      data-testid={`submission-${submission.id}`}
    >
      <div className="flex items-start gap-4">
        {/* Vote Button */}
        <button
          onClick={() => onVote(submission.id)}
          disabled={votingId === submission.id}
          className={`flex flex-col items-center gap-1 p-3 rounded-lg border transition-colors ${
            submission.voted_by?.includes(user?.id)
              ? 'bg-primary/20 border-primary/50 text-primary'
              : 'bg-secondary border-border text-slate-400 hover:text-white hover:border-white/20'
          }`}
        >
          {votingId === submission.id ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <ThumbsUp className="w-5 h-5" />
          )}
          <span className="font-bold text-lg">{submission.votes || 0}</span>
        </button>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="font-heading text-xl font-semibold text-white">
              {submission.name}
            </h3>
            <Badge className={`${getStatusBadge(submission.status)} capitalize`}>
              {submission.status}
            </Badge>
            <Badge variant="outline" className="border-slate-700 text-slate-300">
              {submission.industry}
            </Badge>
          </div>
          
          <p className="text-slate-400 mb-3">{submission.description}</p>
          
          <div className="flex items-center gap-4 text-sm text-slate-500">
            <span>Submitted by {submission.submitted_by_name}</span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {new Date(submission.created_at).toLocaleDateString()}
            </span>
          </div>
        </div>

        {/* View Details */}
        {submission.status === 'approved' && (
          <Link to={`/marketplace?search=${submission.name}`}>
            <Button variant="ghost" className="text-primary">
              View
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
};

export default CommunityPage;
